import { SupplierFormFields } from "@/components/suppliers/SupplierFormFields";
import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { PartyUpsertDialog } from "@/components/parties/PartyUpsertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useGridPagination } from "@/hooks/useGridPagination";
import { partyEntityKindLabel } from "@/lib/parties/entityKind";
import {
  createSupplier,
  deleteSupplier,
  emptySupplierDraft,
  formatSupplierDisplayName,
  isSupplierDraftComplete,
  listSuppliers,
  normalizeSupplierDraft,
  normalizeSupplierType,
  recordToSupplierDraft,
  SUPPLIER_TYPE_LABELS,
  SUPPLIER_TYPE_VALUES,
  supplierErrorMessage,
  supplierTypeLabel,
  syncSuppliersFromApi,
  updateSupplier,
  validateSupplierDraft,
  type SupplierDraft,
  type SupplierRecord,
} from "@/lib/suppliers/suppliersStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { BookOpen, CalendarIcon, Edit, Eye, Filter, LayoutGrid, List, Loader2, MapPin, Plus, Search, Trash2, Truck, Wallet } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const defaultDraft: SupplierDraft = emptySupplierDraft();

const SUPPLIERS_PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

type SupplierSortBy = "latest" | "oldest" | "name_asc" | "name_desc" | "status_active_first" | "status_inactive_first";

function formatCreatedAtDisplay(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

function formatSupplierBalance(amount: number, _side?: "Dr" | "Cr", currency?: string): string {
  if (!Number.isFinite(amount)) return "—";
  return formatCurrency(Math.abs(amount), currency);
}

const Suppliers = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "supplier_can_view");
  const canCreate = can(user, "supplier_can_create");
  const canUpdate = can(user, "supplier_can_update");
  const canDelete = can(user, "supplier_can_delete");
  const canOpenLedger =
    can(user, "supplier_can_view_any") ||
    canView ||
    can(user, "chart_of_account_can_view_any");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [viewSupplier, setViewSupplier] = useState<SupplierRecord | null>(null);
  const [editing, setEditing] = useState<SupplierRecord | null>(null);
  const [draft, setDraft] = useState<SupplierDraft>(defaultDraft);
  const [deleteTarget, setDeleteTarget] = useState<SupplierRecord | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = useState<SupplierSortBy>("latest");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterCityId, setFilterCityId] = useState<string>("__all__");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [filterName, setFilterName] = useState("");
  const [filterContactPerson, setFilterContactPerson] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterPhone, setFilterPhone] = useState("");
  const [filterTypeId, setFilterTypeId] = useState<string>("__all__");
  const [filterStatusId, setFilterStatusId] = useState<string>("__all__");
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");

  const suppliers = useMemo(() => listSuppliers(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const cityOptions = useMemo(() => {
    const set = new Set<string>();
    for (const s of suppliers) {
      const city = s.city?.trim();
      if (city) set.add(city);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [suppliers]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsBootstrapping(true);
      setIsForbidden(false);
      try {
        await syncSuppliersFromApi();
        if (mounted) refresh();
      } catch (error) {
        if (mounted && isAxiosError(error) && error.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error("[Suppliers] Load failed", error);
        if (mounted) toast.error("Could not fetch suppliers. Showing cached data if any.");
      } finally {
        if (mounted) setIsBootstrapping(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return suppliers.filter((s) => {
      if (filterCityId !== "__all__") {
        const city = (s.city ?? "").trim();
        if (city !== filterCityId) return false;
      }
      if (filterTypeId !== "__all__") {
        if (normalizeSupplierType(s.type) !== filterTypeId) return false;
      }
      if (filterStatusId !== "__all__") {
        if (s.status !== filterStatusId) return false;
      }
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(s.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(s.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      const nameNeedle = filterName.trim().toLowerCase();
      if (nameNeedle && !s.name.toLowerCase().includes(nameNeedle)) return false;
      const contactNeedle = filterContactPerson.trim().toLowerCase();
      if (contactNeedle && !(s.contactPerson ?? "").toLowerCase().includes(contactNeedle)) return false;
      const emailNeedle = filterEmail.trim().toLowerCase();
      if (emailNeedle && !(s.email ?? "").toLowerCase().includes(emailNeedle)) return false;
      const phoneNeedle = filterPhone.replace(/\s/g, "").toLowerCase();
      if (phoneNeedle && !(s.phone ?? "").replace(/\s/g, "").toLowerCase().includes(phoneNeedle)) return false;
      if (!q) return true;
      const typeLabel = supplierTypeLabel(s.type).toLowerCase();
      return (
        s.name.toLowerCase().includes(q) ||
        s.fatherName.toLowerCase().includes(q) ||
        s.cnic.toLowerCase().includes(q) ||
        s.address.toLowerCase().includes(q) ||
        (s.contactPerson ?? "").toLowerCase().includes(q) ||
        (s.phone ?? "").toLowerCase().includes(q) ||
        (s.email ?? "").toLowerCase().includes(q) ||
        (s.city ?? "").toLowerCase().includes(q) ||
        (s.type ?? "").toLowerCase().includes(q) ||
        typeLabel.includes(q)
      );
    });
  }, [
    suppliers,
    query,
    filterCityId,
    filterCreatedDate,
    filterName,
    filterContactPerson,
    filterEmail,
    filterPhone,
    filterTypeId,
    filterStatusId,
    quickDate,
  ]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "latest":
          return b.createdAt.localeCompare(a.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "status_inactive_first": {
          if (a.status !== b.status) return a.status === "Inactive" ? -1 : 1;
          return b.createdAt.localeCompare(a.createdAt);
        }
        case "status_active_first": {
          if (a.status !== b.status) return a.status === "Active" ? -1 : 1;
          return b.createdAt.localeCompare(a.createdAt);
        }
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCityId !== "__all__" ? 1 : 0) +
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (filterContactPerson.trim() ? 1 : 0) +
    (filterEmail.trim() ? 1 : 0) +
    (filterPhone.trim() ? 1 : 0) +
    (filterTypeId !== "__all__" ? 1 : 0) +
    (filterStatusId !== "__all__" ? 1 : 0);

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCityId}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${filterContactPerson}|${filterEmail}|${filterPhone}|${filterTypeId}|${filterStatusId}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: SUPPLIERS_PAGE_SIZE_OPTIONS,
    defaultPageSize: SUPPLIERS_PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    if (!canCreate) {
      toast.error("You don’t have permission to create suppliers.");
      return;
    }
    setEditing(null);
    setDraft({ ...defaultDraft });
    setFormOpen(true);
  };

  const openEdit = (s: SupplierRecord) => {
    if (!canUpdate) {
      toast.error("You don’t have permission to edit suppliers.");
      return;
    }
    setEditing(s);
    setDraft(recordToSupplierDraft(s));
    setFormOpen(true);
  };

  const saveSupplier = async () => {
    if (isSaving) return;
    if (editing && !canUpdate) {
      toast.error("You don’t have permission to edit suppliers.");
      return;
    }
    if (!editing && !canCreate) {
      toast.error("You don’t have permission to create suppliers.");
      return;
    }
    const validation = validateSupplierDraft(draft);
    if (!validation.valid) {
      toast.error(validation.message ?? "Complete all required supplier fields.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = normalizeSupplierDraft(draft);
      if (editing) {
        const result = await updateSupplier(editing.id, payload);
        if (!result) {
          toast.error("Supplier not found.");
          return;
        }
        toast.success(result.message);
      } else {
        const result = await createSupplier(payload);
        toast.success(result.message);
      }
      setFormOpen(false);
      setEditing(null);
      try {
        await syncSuppliersFromApi();
      } catch (syncError) {
        console.error("[Suppliers] Re-sync after save failed", syncError);
      }
      refresh();
    } catch (error) {
      console.error("[Suppliers] Save failed", error);
      toast.error(supplierErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    if (!canDelete) {
      toast.error("You don’t have permission to delete suppliers.");
      setDeleteTarget(null);
      return;
    }
    setIsDeleting(true);
    try {
      const result = await deleteSupplier(deleteTarget.id);
      setDeleteTarget(null);
      try {
        await syncSuppliersFromApi();
      } catch (syncError) {
        console.error("[Suppliers] Re-sync after delete failed", syncError);
      }
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[Suppliers] Delete failed", error);
      toast.error(supplierErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Suppliers access required"
      message="You don’t have permission to view suppliers. Ask an administrator to grant supplier_can_view_any."
    />
  ) : (
    <Layout title="Suppliers">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Suppliers</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage car suppliers and dealers</p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" /> Add Supplier
            </Button>
          ) : null}
        </div>

        <PartyUpsertDialog
          open={formOpen}
          onOpenChange={(o) => {
            setFormOpen(o);
            if (!o) setEditing(null);
          }}
          title={editing ? "Edit supplier" : "Add supplier"}
          description={
            editing
              ? "Update this supplier's details and contact information."
              : "Add a supplier to your directory with contact and address details."
          }
          icon={Truck}
          accent="amber"
          footer={
            <>
              <Button variant="outline" onClick={() => setFormOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                onClick={() => void saveSupplier()}
                disabled={isSaving || !isSupplierDraftComplete(draft)}
                className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {editing ? "Save supplier" : "Add supplier"}
              </Button>
            </>
          }
        >
          <SupplierFormFields draft={draft} onChange={setDraft} disabled={isSaving} />
        </PartyUpsertDialog>

        <Dialog open={!!viewSupplier} onOpenChange={(o) => !o && setViewSupplier(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Supplier details</DialogTitle>
            </DialogHeader>
            {viewSupplier ? (
              <dl className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">
                    {viewSupplier.entityKind === "business" ? "Business Name" : "Supplier Name with Father Name"}
                  </dt>
                  <dd className="font-medium text-foreground">{formatSupplierDisplayName(viewSupplier)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Supplier Type</dt>
                  <dd className="text-muted-foreground">{partyEntityKindLabel(viewSupplier.entityKind)}</dd>
                </div>
                {viewSupplier.entityKind === "business" ? (
                  <div>
                    <dt className="text-xs text-muted-foreground">NTN / Registration No.</dt>
                    <dd className="text-muted-foreground">{viewSupplier.ntn || "—"}</dd>
                  </div>
                ) : (
                  <div>
                    <dt className="text-xs text-muted-foreground">CNIC #</dt>
                    <dd className="text-muted-foreground">{viewSupplier.cnic || "—"}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs text-muted-foreground">Contact Number</dt>
                  <dd className="text-muted-foreground">{viewSupplier.phone || "—"}</dd>
                </div>
                {viewSupplier.entityKind === "business" ? (
                  <div>
                    <dt className="text-xs text-muted-foreground">Contact person</dt>
                    <dd className="text-muted-foreground">{viewSupplier.contactPerson || "—"}</dd>
                  </div>
                ) : null}
                <div>
                  <dt className="text-xs text-muted-foreground">Email</dt>
                  <dd className="text-muted-foreground">{viewSupplier.email || "—"}</dd>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">Country</dt>
                    <dd className="text-muted-foreground">{viewSupplier.countryName || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">State</dt>
                    <dd className="text-muted-foreground">{viewSupplier.stateName || "—"}</dd>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">City</dt>
                    <dd className="text-muted-foreground">{viewSupplier.city || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Postal Code</dt>
                    <dd className="text-muted-foreground">{viewSupplier.postalCode || "—"}</dd>
                  </div>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Address Line 1</dt>
                  <dd className="text-muted-foreground whitespace-pre-wrap">{viewSupplier.addressLine1 || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Address Line 2</dt>
                  <dd className="text-muted-foreground whitespace-pre-wrap">{viewSupplier.addressLine2 || "—"}</dd>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">Type</dt>
                    <dd className="text-muted-foreground">{supplierTypeLabel(viewSupplier.type)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Status</dt>
                    <dd className="text-muted-foreground">{viewSupplier.status}</dd>
                  </div>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Balance</dt>
                  <dd
                    className={
                      viewSupplier.balanceSide === "Cr" && Math.abs(viewSupplier.balance) >= 0.005
                        ? "text-amber-700 dark:text-amber-400 font-medium"
                        : "text-primary font-medium"
                    }
                  >
                    {formatSupplierBalance(
                      viewSupplier.balance,
                      viewSupplier.balanceSide,
                      viewSupplier.currencyCode || undefined,
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Created</dt>
                  <dd className="text-muted-foreground">{formatCreatedAtDisplay(viewSupplier.createdAt)}</dd>
                </div>
              </dl>
            ) : null}
            <DialogFooter>
              <Button variant="glass" onClick={() => setViewSupplier(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, CNIC, phone, address, email, city, type..."
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter suppliers</SheetTitle>
                  <SheetDescription>Refine by name, contact, email, phone, city, type, status, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Contact</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Supplier name contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        placeholder="e.g. Ali Motors"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Contact person contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterContactPerson}
                        onChange={(e) => setFilterContactPerson(e.target.value)}
                        placeholder="e.g. Hassan"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Email contains</Label>
                      <Input
                        className="glass-input h-10"
                        type="text"
                        inputMode="email"
                        value={filterEmail}
                        onChange={(e) => setFilterEmail(e.target.value)}
                        placeholder="e.g. gmail.com"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Phone contains</Label>
                      <Input
                        className="glass-input h-10"
                        type="tel"
                        value={filterPhone}
                        onChange={(e) => setFilterPhone(e.target.value)}
                        placeholder="e.g. 300 or +92"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">City</Label>
                      <Select value={filterCityId} onValueChange={setFilterCityId}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="All cities" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All cities</SelectItem>
                          {cityOptions.map((city) => (
                            <SelectItem key={city} value={city}>
                              {city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Type</Label>
                      <Select value={filterTypeId} onValueChange={setFilterTypeId}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="All types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All types</SelectItem>
                          {SUPPLIER_TYPE_VALUES.map((value) => (
                            <SelectItem key={value} value={value}>
                              {SUPPLIER_TYPE_LABELS[value]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <Select value={filterStatusId} onValueChange={setFilterStatusId}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="All statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All statuses</SelectItem>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Created date</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "glass-input h-10 w-full justify-start text-left font-normal",
                            !filterCreatedDate && "text-muted-foreground",
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                          {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                        <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                        <div className="flex gap-2 border-t p-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="flex-1"
                            onClick={() => setFilterCreatedDate(undefined)}
                          >
                            Clear
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button
                    type="button"
                    variant="glass"
                    className="w-full"
                    onClick={() => {
                      setFilterCityId("__all__");
                      setFilterCreatedDate(undefined);
                      setFilterName("");
                      setFilterContactPerson("");
                      setFilterEmail("");
                      setFilterPhone("");
                      setFilterTypeId("__all__");
                      setFilterStatusId("__all__");
                    }}
                  >
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="suppliers-page-size"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={SUPPLIERS_PAGE_SIZE_OPTIONS}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as SupplierSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="status_active_first">Status: Active first</SelectItem>
                  <SelectItem value="status_inactive_first">Status: Inactive first</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="suppliers-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={SUPPLIERS_PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as SupplierSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="status_active_first">Status: Active first</SelectItem>
                    <SelectItem value="status_inactive_first">Status: Inactive first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {isBootstrapping ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading suppliers…
          </div>
        ) : null}

        {!isBootstrapping && filtered.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {suppliers.length === 0
              ? canCreate
                ? "No suppliers yet. Click “Add Supplier” to create one."
                : "No suppliers yet."
              : "No suppliers match your search."}
          </div>
        ) : null}

        {!isBootstrapping && filtered.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <CatalogEntityCard
                  title={formatSupplierDisplayName(s)}
                  titleIcon={Truck}
                  wrapText
                  metaColumns={2}
                  primaryMeta={{
                    label: "City",
                    value: s.city || "—",
                    icon: MapPin,
                    iconClassName: "text-emerald-600",
                  }}
                  secondaryMeta={{
                    label: "Balance",
                    value: formatSupplierBalance(s.balance, s.balanceSide, s.currencyCode || undefined),
                    icon: Wallet,
                    iconClassName:
                      s.balanceSide === "Cr" && Math.abs(s.balance) >= 0.005
                        ? "text-amber-600"
                        : "text-primary",
                  }}
                  footer={`Last updated: ${formatCatalogAuditDate(s.updatedAt)}`}
                  footerRight={s.status}
                  footerRightClassName={s.status === "Active" ? "text-success font-medium" : "text-muted-foreground"}
                  onView={canView ? () => setViewSupplier(s) : undefined}
                  viewIconOnly
                  onEdit={canUpdate ? () => openEdit(s) : undefined}
                  editIconOnly
                  onLedger={
                    canOpenLedger
                      ? () => navigate(`/parties/ledger?type=supplier&party_id=${encodeURIComponent(s.id)}`)
                      : undefined
                  }
                  onDelete={canDelete ? () => setDeleteTarget(s) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && filtered.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <Truck className="w-8 h-8 text-muted-foreground/40" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground truncate">{formatSupplierDisplayName(s)}</h3>
                      <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-emerald-500/20 text-emerald-600">
                        {s.city?.trim() || "—"}
                      </span>
                      <span
                        className={cn(
                          "text-[11px] px-2 py-0.5 rounded-full font-medium",
                          s.status === "Active" ? "bg-success/15 text-success" : "bg-muted text-muted-foreground",
                        )}
                      >
                        {s.status}
                      </span>
                      {s.currencyCode ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                          {s.currencyCode}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {[s.phone, s.cnic].filter(Boolean).join(" · ") || "—"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{supplierTypeLabel(s.type)}</p>
                  </div>
                  <div className="text-right ml-auto">
                    <p
                      className={cn(
                        "text-sm font-semibold tabular-nums",
                        s.balanceSide === "Cr" && Math.abs(s.balance) >= 0.005
                          ? "text-amber-700 dark:text-amber-400"
                          : "text-primary",
                      )}
                    >
                      {formatSupplierBalance(s.balance, s.balanceSide, s.currencyCode || undefined)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">{formatCreatedAtDisplay(s.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canView ? (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 shrink-0 p-0"
                            onClick={() => setViewSupplier(s)}
                            aria-label="View Details"
                          >
                            <Eye className="w-3.5 h-3.5 shrink-0" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">View Details</TooltipContent>
                      </Tooltip>
                    ) : null}
                    {canUpdate ? (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 shrink-0 p-0"
                            onClick={() => openEdit(s)}
                            aria-label="Edit"
                          >
                            <Edit className="w-3.5 h-3.5 shrink-0" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Edit</TooltipContent>
                      </Tooltip>
                    ) : null}
                    {canOpenLedger ? (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 shrink-0 p-0"
                            onClick={() => navigate(`/parties/ledger?type=supplier&party_id=${encodeURIComponent(s.id)}`)}
                            aria-label="Ledger"
                          >
                            <BookOpen className="w-3.5 h-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Ledger</TooltipContent>
                      </Tooltip>
                    ) : null}
                    {canDelete ? (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 shrink-0 border-destructive/60 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setDeleteTarget(s)}
                            aria-label="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Delete</TooltipContent>
                      </Tooltip>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && filtered.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this supplier?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete{" "}
                <strong className="text-foreground font-semibold">{deleteTarget.name}</strong>? This cannot be undone.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
};

export default Suppliers;
